import {
  type Address,
  type BlockTag,
  type CallParameters,
  type Client,
  createClient,
  formatTransactionRequest,
  type Hash,
  type Hex,
  RpcRequestError,
  type Transport,
  zeroAddress,
} from "viem";


// Call kinds returned by Geth's built-in callTracer.
export type CallTracerCallType =
  | "CALL"
  | "STATICCALL"
  | "DELEGATECALL"
  | "CREATE"
  | "CREATE2"
  | "SELFDESTRUCT"
  | "SUICIDE"; // legacy alias for SELFDESTRUCT on some nodes

// Log entry when tracerConfig.withLog = true.
export type CallTracerLog = {
  address: Address;
  topics: Hex[];
  data: Hex;
  position: Hex; // log index within the tx
};

/**
 * One node in the call tree. The RPC returns the top-level (depth-0) frame;
 * every sub-call is nested under `calls`.
 * @see https://geth.ethereum.org/docs/developers/evm-tracing/built-in-tracers#calltracer
 */
export type CallTracerFrame = {
  type: CallTracerCallType; // EVM call kind at this frame
  from: Address; // Call initiator
  to?: Address; // Target, meaning depends on call type
  value?: Hex; // Wei sent; sometimes absent if 0
  gas?: Hex; // Gas supplied (hex-encoded)
  gasUsed?: Hex; // Gas consumed including nested (hex-encoded)
  input?: Hex; // Calldata
  output?: Hex; // Return data (even if reverted)
  error?: string; // Set if this frame failed
  revertReason?: string; // Decoded Solidity revert reason (if available)
  calls?: CallTracerFrame[]; // Nested sub-calls
  logs?: CallTracerLog[]; // Only when tracerConfig.withLog = true
};

// Return value of debug.traceTransaction(..., { tracer: "callTracer" }).
export type DebugTraceTransactionCallTracerResult = CallTracerFrame;

// RPC request shape (for reference).
export type DebugTraceTransactionCallTracerRequest = {
  tracer: "callTracer";
  tracerConfig?: {
    onlyTopCall?: boolean; // If true, omit nested calls (only the root frame)
    withLog?: boolean; // If true, include `logs` per frame
  };
};

type CallTracerOptions = Omit<DebugTraceTransactionCallTracerRequest, "tracer">;

type TraceCallClient = {
  traceCall: (
    args: CallParameters,
    blockTag?: BlockTag,
    config?: CallTracerOptions,
  ) => Promise<CallTracerFrame>;
};


export const isMethodNotFound = (err: unknown): boolean => {
  if (err instanceof RpcRequestError && err.code === -32601) return true;
  const msg = String((err as { message?: unknown })?.message ?? err).toLowerCase();
  return msg.includes("method not found");
};


export async function probeDebugTraceSupport(
  debugClient: Pick<TraceCallClient, "traceCall">,
): Promise<boolean> {
  try {
    await debugClient.traceCall({ to: zeroAddress, data: "0x" });
    return true;
  } catch (err) {
    if (isMethodNotFound(err)) return false;
    throw err;
  }
}


type DebugRpcRequest = (args: {
  method: "debug_traceCall" | "debug_traceTransaction";
  params: unknown[];
}) => Promise<CallTracerFrame>;


export const debugActions = <T extends Client>(client: T) => {
  const request = client.request as unknown as DebugRpcRequest;

  const traceCall = async (
    args: CallParameters,
    blockTag: BlockTag = "latest",
    config?: CallTracerOptions,
  ): Promise<CallTracerFrame> => {
    return request({
      method: "debug_traceCall",
      params: [
        formatTransactionRequest(args as Parameters<typeof formatTransactionRequest>[0]),
        blockTag,
        { tracer: "callTracer", ...config },
      ],
    });
  };

  return {
    traceTransaction: async (
      hash: Hash,
      config?: CallTracerOptions,
    ): Promise<CallTracerFrame> => {
      return request({
        method: "debug_traceTransaction",
        params: [hash, { tracer: "callTracer", ...config }],
      });
    },
    traceCall,
    supportsDebugTrace: () => probeDebugTraceSupport({ traceCall }),
  };
};


export type DebugClient = ReturnType<typeof createDebugClient>;


export const createDebugClient = (params: {
  chain: NonNullable<Client["chain"]>;
  transport: Transport;
}) => createClient(params).extend(debugActions);
